import { Link } from '@tanstack/react-router'
import { Fragment } from 'react'
import type { useHeroSectionModel } from '@/components/home/components/hero-section/hero-section.model'
import { ContactCtaViewModel } from '@/components/shared/contact-cta/contact-cta.view-model'

type HeroSectionViewProps = ReturnType<typeof useHeroSectionModel>

export function HeroSectionView({
  eyebrowText,
  wordmarkAriaLabel,
  wordmarkChars,
  sloganText,
  sloganWords,
  hintText,
  catalogActionLabel,
  contactActionLabel,
  orientationText,
  orientationRevealDelay,
  triggerText,
  onContinueClick,
  isRevealed,
  actionsRevealDelay,
  isHintVisible,
  stageInert,
  stageStyle,
}: HeroSectionViewProps) {
  const revealModifier = isRevealed ? ' fz-hero__reveal' : ''

  return (
    <section className="fz-hero">
      <div
        className="fz-hero__stage"
        inert={stageInert || undefined}
        style={stageStyle}
      >
        <p
          className={`fz-hero__orientation${revealModifier}`}
          style={{ animationDelay: orientationRevealDelay }}
        >
          {orientationText}
        </p>
        <h1 className="fz-hero__wordmark">
          <span
            className={`fz-hero__eyebrow${revealModifier}`}
            style={{ animationDelay: '0.8s' }}
          >
            {eyebrowText}
          </span>
          <span className="fz-sr-only">{wordmarkAriaLabel}</span>
          <span className="fz-hero__flora" aria-hidden="true">
            {wordmarkChars.map((char) => (
              <span
                key={char.key}
                className={`fz-hero__char${revealModifier}`}
                style={{ animationDelay: char.delay }}
              >
                {char.value}
              </span>
            ))}
          </span>
        </h1>
        <p className="fz-hero__slogan">
          <span className="fz-sr-only">{sloganText}</span>
          <span className="fz-hero__slogan-visual" aria-hidden="true">
            {sloganWords.map((word, wordIndex) => (
              <Fragment key={word.key}>
                {wordIndex > 0 ? ' ' : null}
                <span className="fz-hero__word">
                  {word.chars.map((char) => (
                    <span
                      key={char.key}
                      className={`fz-hero__char${revealModifier}`}
                      style={{ animationDelay: char.delay }}
                    >
                      {char.value}
                    </span>
                  ))}
                </span>
              </Fragment>
            ))}
          </span>
        </p>
        <div
          className={`fz-hero__actions${revealModifier}`}
          style={{ animationDelay: actionsRevealDelay }}
        >
          <Link
            to="/catalogo"
            className="fz-hero__action fz-hero__action--catalog"
          >
            {catalogActionLabel}
          </Link>
          <ContactCtaViewModel label={contactActionLabel} />
          <p className="fz-hero__trigger">{triggerText}</p>
        </div>
        <button
          type="button"
          className={`fz-hero__hint${isHintVisible ? '' : ' fz-hero__hint--hidden'}`}
          inert={!isHintVisible || undefined}
          onClick={onContinueClick}
        >
          <svg
            className="fz-hero__hint-petal"
            viewBox="0 0 24 40"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              d="M12 2 C 4 10, 2 22, 12 30 C 22 22, 20 10, 12 2 Z"
            />
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="1.1"
              d="M12 32 L12 38 M12 38 L8.5 34.5 M12 38 L15.5 34.5"
            />
          </svg>
          <span className="fz-hero__hint-label">{hintText}</span>
        </button>
      </div>
    </section>
  )
}
